<template>
  <div class="row">
    <div class="col-md-12">
      <!-- Loading State -->
      <div v-if="isLoading" class="text-center py-5">
        <b-spinner class="align-middle"></b-spinner>
        <span class="ml-2">Loading NOC details...</span>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="text-center py-5">
        <div class="alert alert-danger">
          <i class="fas fa-exclamation-triangle mr-2"></i>
          {{ error }}
        </div>
        <router-link to="/noc-mappings" class="btn btn-secondary">
          <i class="fas fa-arrow-left mr-1"></i> Back to NOC Mappings
        </router-link>
      </div>

      <template v-else>
        <!-- Back Link -->
        <div class="mb-3">
          <router-link to="/noc-mappings" class="text-muted">
            <i class="fas fa-arrow-left mr-1"></i> Back to NOC Mappings
          </router-link>
        </div>

        <!-- NOC Info Card -->
        <div class="card mb-3">
          <div class="card-header">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h4 class="card-title mb-0">
                  <code class="mr-2">{{ nocNumber.code }}</code>
                  {{ nocNumber.name_en }}
                </h4>
                <small class="text-muted">{{ nocNumber.name_fr }}</small>
              </div>
              <b-badge variant="primary" pill class="px-3 py-2">
                NOC 2021
              </b-badge>
            </div>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-3">
                <div class="text-center">
                  <h3 class="mb-0">{{ stats.total_job_titles }}</h3>
                  <small class="text-muted">Job Titles</small>
                </div>
              </div>
              <div class="col-md-3">
                <div class="text-center">
                  <h3 class="mb-0">{{ stats.total_offers_using }}</h3>
                  <small class="text-muted">Offers Using</small>
                </div>
              </div>
              <div class="col-md-3">
                <div class="text-center">
                  <h3 class="mb-0">{{ stats.manual_overrides }}</h3>
                  <small class="text-muted">Manual Overrides</small>
                </div>
              </div>
              <div class="col-md-3">
                <div class="text-center">
                  <h3 class="mb-0">{{ formatConfidence(stats.avg_ai_confidence) }}</h3>
                  <small class="text-muted">Avg AI Confidence</small>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Job Titles Card -->
        <div class="card">
          <div class="card-header">
            <div class="d-flex justify-content-between align-items-center">
              <h5 class="mb-0">Job Titles</h5>
              <small class="text-muted">
                These are the titles AI can choose from for this NOC code
              </small>
            </div>
            <b-form-input
              v-model="search"
              placeholder="Search job titles..."
              class="mt-2"
              @input="debouncedSearch"
            />
          </div>
          <div class="card-body p-0">
            <b-table
              :items="jobTitles.data"
              :fields="tableFields"
              :busy="isSearching"
              hover
              responsive
              show-empty
            >
              <template #table-busy>
                <div class="text-center my-4">
                  <b-spinner small class="mr-2"></b-spinner>
                  Searching...
                </div>
              </template>
              <template #empty>
                <div class="text-center text-muted my-4">
                  No job titles found
                </div>
              </template>
              <template #cell(id)="{ item }">
                <code>{{ item.id }}</code>
              </template>
              <template #cell(title_en)="{ item }">
                {{ item.title_en }}
              </template>
              <template #cell(title_fr)="{ item }">
                {{ item.title_fr }}
              </template>
            </b-table>
          </div>
          <div class="card-footer">
            <div class="d-flex justify-content-between align-items-center">
              <small class="text-muted">
                Showing {{ jobTitles.from || 0 }}-{{ jobTitles.to || 0 }} of {{ jobTitles.total || 0 }} titles
              </small>
              <b-pagination
                v-if="jobTitles.last_page > 1"
                v-model="currentPage"
                :total-rows="jobTitles.total"
                :per-page="jobTitles.per_page"
                @change="changePage"
                size="sm"
                class="mb-0"
              />
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import {
  BSpinner,
  BBadge,
  BFormInput,
  BTable,
  BPagination
} from "bootstrap-vue";
import debounce from "lodash/debounce";

export default {
  components: {
    BSpinner,
    BBadge,
    BFormInput,
    BTable,
    BPagination
  },
  props: {
    nocCode: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      isLoading: true,
      isSearching: false,
      error: null,
      nocNumber: {},
      stats: {},
      jobTitles: {
        data: [],
        total: 0,
        per_page: 50,
        current_page: 1,
        last_page: 1
      },
      search: "",
      currentPage: 1,
      tableFields: [
        { key: "id", label: "ID", thStyle: { width: "80px" } },
        { key: "title_en", label: "English Title" },
        { key: "title_fr", label: "French Title" }
      ]
    };
  },
  async created() {
    this.debouncedSearch = debounce(this.fetchJobTitles, 300);
    await this.fetchNocDetail();
    this.isLoading = false;
  },
  methods: {
    async fetchNocDetail() {
      try {
        const { data } = await this.$http.get(
          `/enroll_admin/noc-numbers/${this.nocCode}`
        );
        this.nocNumber = data.noc_number;
        this.stats = data.stats;
        this.jobTitles = data.job_titles;
      } catch (err) {
        if (err.response?.status === 404) {
          this.error = `NOC code ${this.nocCode} not found`;
        } else {
          this.error = "Failed to load NOC details";
        }
      }
    },
    async fetchJobTitles() {
      this.isSearching = true;
      try {
        const params = {
          page: this.currentPage,
          per_page: 50
        };
        if (this.search) {
          params.search = this.search;
        }
        const { data } = await this.$http.get(
          `/enroll_admin/noc-numbers/${this.nocCode}`,
          { params }
        );
        this.jobTitles = data.job_titles;
      } catch (err) {
        this.$bvToast.toast("Failed to search job titles", {
          title: "Error",
          variant: "danger",
          solid: true
        });
      } finally {
        this.isSearching = false;
      }
    },
    changePage(page) {
      this.currentPage = page;
      this.fetchJobTitles();
    },
    formatConfidence(confidence) {
      if (confidence === null || confidence === undefined) {
        return "N/A";
      }
      return `${(confidence * 100).toFixed(0)}%`;
    }
  }
};
</script>

<style scoped>
.card-title {
  font-size: 1rem;
  margin-bottom: 0;
}
code {
  font-size: 1.1em;
  background-color: #f8f9fa;
  padding: 2px 6px;
  border-radius: 3px;
}
</style>
